import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { DynamicDialogRef } from 'primeng/dynamicdialog';

import { GridActions, GridService } from '@core/services';
import { remToPx } from '@util/layout';

import { Filters } from '#types/grid';
import { SupplierLookupResponseRow } from '#types/shipment';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@shipment/constants';
import { ShipmentService } from '@shipment/services';

@Component({
  selector: 'z-supplier-lookup-dialog',
  templateUrl: './supplier-lookup-dialog.component.html',
  styleUrls: ['./supplier-lookup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupplierLookupDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  stateOrProvinceList$ = this.shipmentService.stateOrProvinceDropdownList$;

  form: FormGroup;

  custid: string;
  supplierId: string;

  selectedRow: SupplierLookupResponseRow;

  matchTypes: Record<string, string> = {};

  container: HTMLDivElement;
  containerParent: HTMLDivElement;
  containerFilters: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  lookupId = 'supplier-lookup-dialog';

  gridId = CONSTANTS.GRID_SHIPMENT_SUPPLIER_LOOKUP_DIALOG;

  constructor(
    private store: Store,
    private shipmentService: ShipmentService,
    private ref: DynamicDialogRef,
    private gridService: GridService,
    private authService: AuthService,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    this.shipmentService.supplierLookupDialogSelectedCustomer$.pipe(take(1)).subscribe((custid) => {
      this.custid = custid;
    });

    this.form = new FormGroup({
      supplierId: new FormControl(),
      name: new FormControl(),
      stateOrProvince: new FormControl(),
      activeStatusOnly: new FormControl('Y'),
    });

    this.shipmentService.requestLists();

    this.gridService
      .selectedRow$<SupplierLookupResponseRow>(this.gridId)
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedRow) => {
        this.selectedRow = selectedRow.data;
        this.supplierId = this.selectedRow.shipper;

        if (selectedRow.selectionType === 'double') {
          this.onSelectClick();
        }
      });

    // close dialog if no longer authenticated
    this.authService.authenticated$
      .pipe(
        filter((authenticated) => !authenticated),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onCancelClick();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  initResizeObserver() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        const header: HTMLDivElement = this.containerParent.querySelector('.p-dialog-header');
        const filters: HTMLDivElement = this.container.querySelector('.filters');
        const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
        const paginator: HTMLDivElement = this.container.querySelector('.p-paginator');

        let offset = 0;

        if (header) {
          offset += Math.floor(header.offsetHeight);
        }

        if (filters) {
          offset += Math.floor(filters.offsetHeight);
        }

        if (footer) {
          offset += Math.floor(footer.offsetHeight);
        }

        if (paginator) {
          offset += Math.floor(paginator.offsetHeight);
        }

        this.shipmentService.supplierLookupDialogDimensions$.next({
          container: {
            width: Math.floor(this.containerParent.offsetWidth),
            height: Math.floor(this.containerParent.offsetHeight),
          },
          grid: {
            width: Math.floor(this.containerParent.offsetWidth) - remToPx('2rem'),
            height: Math.floor(this.containerParent.offsetHeight) - offset,
          },
        });
      });
    });

    try {
      this.container = document.querySelector(`.${this.lookupId}`);

      if (this.container) {
        this.containerParent = this.container.closest(`.lookup-dialog`);

        if (this.containerParent) {
          this.containerResizeObserver.observe(this.containerParent);
        }

        this.containerFilters = this.container.querySelector('.filters');

        if (this.containerFilters) {
          this.containerResizeObserver.observe(this.containerFilters);
        }
      }
    } catch {}
  }

  destroyResizeObserver() {
    try {
      if (this.containerResizeObserver && this.containerParent) {
        this.containerResizeObserver.unobserve(this.containerParent);
      }

      if (this.containerResizeObserver && this.containerFilters) {
        this.containerResizeObserver.unobserve(this.containerFilters);
      }
    } catch {}
  }

  onSelectClick() {
    this.ref.close({ supplierId: this.supplierId, selectedRow: this.selectedRow });
  }

  onCancelClick() {
    this.ref.close(undefined);
  }

  onMatchTypeChange(field: string, matchType: string) {
    this.matchTypes[field] = matchType;
  }

  applyFilters() {
    const filters: Filters = {
      custid: { value: this.custid },
    };

    if (this.form.get('supplierId').value) {
      filters.supplierId = { value: this.form.get('supplierId').value, matchMode: this.matchTypes.supplierId ?? 'equals' };
    }

    if (this.form.get('name').value) {
      filters.name = { value: this.form.get('name').value, matchMode: this.matchTypes.name ?? 'startsWith' };
    }

    if (this.form.get('stateOrProvince').value) {
      filters.stateOrProvince = { value: this.form.get('stateOrProvince').value };
    }

    if (this.form.get('activeStatusOnly').value) {
      filters.activeOnly = { value: this.form.get('activeStatusOnly').value };
    }

    this.store.dispatch(GridActions.SetFilters({ id: this.gridId, filters }));
  }

  reset(): void {
    this.form.setValue({
      supplierId: null,
      name: null,
      stateOrProvince: null,
      activeStatusOnly: 'Y',
    });
  }

  isInvalid(formControlName: string) {
    return !this.form.controls[formControlName].pristine && this.form.controls[formControlName].invalid;
  }
}
